import { buildVideoObjectSchema } from "@marketing-sites/utils/video-object-schema";
import { cuesToPlainText } from "@marketing-sites/utils/transcript";
import {
  fetchWistiaEmbedTranscript,
  wistiaIdFromUrl,
} from "@marketing-sites/utils/wistia-embed";
import JsonLd from "@marketing-sites/utils/json-ld";

import { TranscriptMarkup } from "./transcript-markup";
import type { AEOTranscriptProps } from "./transcript.types";

/**
 * Build-time AEO video tag. Derives the Wistia id from the player's `video.url`,
 * fetches the public embed JSON once at build (no secret, no client fetch), and
 * renders BOTH the hidden transcript and the VideoObject JSON-LD.
 * name/duration/thumbnail/contentUrl come from the same fetch; the author need
 * only pass the `video` object it already gave `MediaPlayer.Context`.
 * Fails soft: renders nothing if the URL has no id or the fetch fails.
 *
 * Async Server Component — render it from a server component; the build-time
 * fetch guarantee holds only server-side, so never render it in a client tree.
 */
export const AEOTranscript = async ({
  video,
  description,
  uploadDate,
  heading,
}: AEOTranscriptProps) => {
  const wistiaId = wistiaIdFromUrl(video.url);
  if (!wistiaId) return null;

  const data = await fetchWistiaEmbedTranscript(wistiaId);
  if (!data) return null;

  const title = video.title || data.name;
  const schema = buildVideoObjectSchema({
    name: title,
    description: description ?? title,
    uploadDate,
    durationSeconds: data.durationSeconds,
    thumbnailUrl: data.thumbnailUrl,
    contentUrl: data.contentUrl,
    transcript: cuesToPlainText(data.cues),
  });

  return (
    <>
      <JsonLd id={`videoobject-${video.id}`} schema={schema} />
      <TranscriptMarkup
        cues={data.cues}
        playerId={video.id}
        heading={heading ?? `Transcript: ${title}`}
      />
    </>
  );
};
